import { FC, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button, Menu, MenuItem } from '@mui/material';
import classNames from 'classnames';

import { INav } from '@interfaces/routes';
import { headerSupplementsNav } from '@routes/header';
import urls from '@routes/urls';
import {
  canAccessSupplementInquirySelector,
  dashboardOrgDataSelector,
} from '@store/features/dashboardOrgInfo/selectors';
import { userDataSelector } from '@store/features/auth/user/selectors';

interface IProps {
  menuLinks: INav[];
}

const AthleteSupplementsMenuItem: FC<IProps> = ({ menuLinks }: IProps): JSX.Element => {
  const { t } = useTranslation();
  const [anchorSupplementsNav, setAnchorSupplementsNav] = useState<null | HTMLElement>(null);

  const location = useLocation();

  const isTabActive = useMemo(() => {
    const pathParts = location.pathname.split('/');
    return pathParts.some(
      part => part.includes('dietary-supplement-inquiry') || part.includes('high-risk-supplements'),
    );
  }, [location.pathname]);

  const handleOpenSupplementsNav = (event: MouseEvent<HTMLElement>): void => {
    setAnchorSupplementsNav(event.currentTarget);
  };

  const handleCloseSupplementsNav = (): void => {
    setAnchorSupplementsNav(null);
  };

  return (
    <>
      <Button
        className={classNames(
          'header__link-w-chevron',
          'header__resources-nav',
          'no-print-and-hide',
          isTabActive && 'header__active-link',
        )}
        variant="text"
        onClick={handleOpenSupplementsNav}
        sx={{ p: 0 }}
        endIcon={<KeyboardArrowDown color="primary" className="header__chevron" />}
      >
        {t('headerNav.supplements')}
      </Button>
      <Menu
        sx={{ mt: '45px' }}
        className="header__supplements-nav no-print-and-hide"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorEl={anchorSupplementsNav}
        id="supplements-nav"
        open={Boolean(anchorSupplementsNav)}
        onClose={handleCloseSupplementsNav}
      >
        {menuLinks.map((nav: INav) => (
          <MenuItem key={nav.path} onClick={handleCloseSupplementsNav}>
            <NavLink key={nav.path} to={nav.path} className="no-print-and-hide">
              {t(nav.i18nTitle)}
            </NavLink>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AthleteSupplementsMenuItem;
